import { FileText, Activity, BarChart3, Target, Layout, Zap, TrendingUp } from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'The strategic picture is scattered',
    description:
      'Finding the current vision, assessment, and priority can take more effort than discussing the decision itself.',
    icon: FileText,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Reviews start with reconstruction',
    description:
      'Pulling together the plan and project updates leaves less time to consider what needs to change.',
    icon: Activity,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Changes lose their delivery context',
    description:
      'A revised priority needs a visible connection to the work and people affected by it.',
    icon: BarChart3,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'Five-step strategy workflow',
    description: 'Move from Foundation through assessments and formulation to the Execution Hub.',
    icon: Target,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'Saved business assessments',
    description:
      'Keep PESTEL, Five Forces, SWOT, driving forces, and success factors with your plan.',
    icon: Layout,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'Connected execution',
    description: 'Trace strategic objectives through their strategies, projects, and tasks.',
    icon: Activity,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'Reports for review',
    description: 'Arrange your strategy in custom visual reports and export PDFs for discussion.',
    icon: BarChart3,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'AI drafting support',
    description: 'Develop objective, strategy, and report drafts from the context you provide.',
    icon: Zap,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Progress views',
    description: 'Switch between hierarchy, list, board, map, and timeline to examine the work.',
    icon: TrendingUp,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function StrategyManagementSoftwarePage() {
  return (
    <KeywordLandingPage
      headline="Keep your strategy connected as the business moves."
      subheadline="Manage your direction, assessments, strategic choices, and delivery in one workspace. See the plan and the work together when it is time to review."
      bullets={[
        'A guided five-step planning workflow',
        'Saved assessments and strategy choices',
        'Connected projects and tasks',
        'Progress views and visual reports',
      ]}
      badgeText="Strategy Management"
      problemTitle="Managing strategy means keeping the pieces together."
      problemSubtitle="A review is harder to prepare when the company direction, decisions, and work updates sit in different places."
      problemCards={problemCards}
      solutionTitle="Give strategy a workspace you can return to."
      solutionSubtitle="StratPlan keeps the foundations, assessments, objectives, and connected work available as you develop and revisit your plan."
      solutionSteps={[
        {
          title: 'Establish the plan',
          description:
            'Work through company direction, external and internal assessment, and strategy formulation in a guided sequence.',
        },
        {
          title: 'Connect the delivery',
          description:
            'Link objectives to strategies, projects, and tasks. Give the work owners, dates, and priorities.',
        },
        {
          title: 'Review with the full picture',
          description:
            'Explore progress views, update the plan as you learn, and prepare a visual report for the next discussion.',
        },
      ]}
      featuresTitle="Keep the plan and its delivery in view"
      features={features}
      finalCtaTitle="Give your strategy a place to develop."
      finalCtaSubtitle="Create your account and bring planning, delivery, and review into one connected workspace."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="strategy_management_landing"
      breadcrumbLabel="Strategy Management"
    />
  );
}
